import {
  CdkPortalOutlet,
  CdkPortalOutletAttachedRef,
  ComponentPortal,
  TemplatePortal
} from '@angular/cdk/portal';
import {
  Component,
  ComponentRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  Type,
  ViewContainerRef,
  Injector,
  Inject
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { SideContentComponent } from './side-content-component';
import { SideMenuService } from './side-menu.service';
import { ToggleSideMenuRequest } from './toggle-side-menu-request';
import { SideMenuAction } from './side-menu-action';
import { Theme } from '../theme-select/theme';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AVAILABLE_THEMES, SIDE_MENU_ACTIONS } from '../../tokens';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { FlexModule } from '@angular/flex-layout/flex';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { NgFor, AsyncPipe } from '@angular/common';
import { MatMenuItem } from '@angular/material/menu';
import { ThemeSelectComponent } from '../theme-select/theme-select.component';
import { RouterLink } from '@angular/router';

export class PortalAndItem {
  constructor(
    public cp: ComponentPortal<any> | TemplatePortal<any>,
    public originalItem: ToggleSideMenuRequest
  ) {}

  attached = false;
}

@Component({
    selector: 'pcp-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    imports: [
        MatTabGroup,
        MatTab,
        FlexModule,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        CdkPortalOutlet,
        MatExpansionPanelTitle,
        MatIcon,
        NgFor,
        MatMenuItem,
        ThemeSelectComponent,
        RouterLink,
        AsyncPipe
    ]
})
export class SideMenuComponent implements OnInit, OnDestroy {
  constructor(
    public service: SideMenuService,
    private viewContainer: ViewContainerRef,
    private defaultInjector: Injector,
    public breakPointObserver: BreakpointObserver,
    @Inject(AVAILABLE_THEMES) public availableThemes: Theme[],
    @Inject(SIDE_MENU_ACTIONS) private actions$: Observable<SideMenuAction[]>
  ) {}

  items = new Array<PortalAndItem>();
  private onDestroy$ = new Subject<void>();
  selectedIndex = 0;
  actions: SideMenuAction[] | null = null;

  @Output()
  changeTheme = new EventEmitter<Theme>();

  ngOnInit() {
    this.actions$.pipe(takeUntil(this.onDestroy$)).subscribe((actions) => {
      actions.sort((a, b) => (a.order <= b.order ? -1 : 1));
      this.actions = actions;
    });
    this.service.sideMenu$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((toggle) => {
        if (!toggle) {
          return;
        }
        if (!toggle.item) {
          if (toggle.selectIndex) {
            if (toggle.selectIndex === Number.MAX_SAFE_INTEGER) {
              this.selectedIndex = this.items.length;
            } else {
              this.selectedIndex = toggle.selectIndex;
            }
          }
          return;
        }

        const item = toggle.item;
        if (
          !this.items.some(
            (i) =>
              !!i &&
              !!i.originalItem &&
              !!i.originalItem.item &&
              i.originalItem.item.id === item.id
          )
        ) {
          this.items.push(
            new PortalAndItem(
              this.isTemplateRef(item.component)
                ? new TemplatePortal<any>(item.component, this.viewContainer)
                : new ComponentPortal(
                    item.component,
                    undefined,
                    item.customInjector ?? this.defaultInjector
                  ),
              toggle
            )
          );
          item.remove$
            .pipe(takeUntil(this.onDestroy$), take(1))
            .subscribe(() => {
              const index = this.items.findIndex(
                (i) => i.originalItem.item === item
              );
              this.items.splice(index, 1);
            });

          if (toggle.toggle && !toggle.selectIndex) {
            this.selectedIndex = this.items.length;
          }
          if (toggle.selectIndex) {
            setTimeout(() => {
              // TODO: check if this timeout can be removed in Angular 6.0.2. check with Insights
              if (toggle.selectIndex === Number.MAX_SAFE_INTEGER) {
                this.selectedIndex = this.items.length;
              } else {
                this.selectedIndex = toggle.selectIndex
                  ? toggle.selectIndex
                  : 0;
              }
            }, 800);
          }
        }
      });
  }

  isTemplateRef(
    component: Type<any> | TemplateRef<any>
  ): component is TemplateRef<any> {
    return (component as TemplateRef<any>)?.createEmbeddedView !== undefined;
  }

  portalAttached(item: PortalAndItem, ref: CdkPortalOutletAttachedRef) {
    if (
      item &&
      ref &&
      item.originalItem &&
      item.originalItem.item &&
      item.originalItem.item.component
    ) {
      if (!this.isTemplateRef(item.originalItem.item.component)) {
        const content = (<ComponentRef<any>>ref)
          .instance as SideContentComponent;
        if (content) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          content.data = item.originalItem.item.data;
        }
      }
    }
  }

  trackByItems(index: number, item: PortalAndItem): string | undefined {
    if (item && item.originalItem && item.originalItem.item) {
      return item.originalItem.item.id;
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }
}
